import React, { useEffect, useMemo, useRef, useState } from "react";

// Derive a 2-letter avatar from the conversation title.
// Skips common Spanish connectors. Falls back to first two letters.
const STOPWORDS = new Set([
  "de", "del", "la", "el", "los", "las", "y", "o", "u", "en", "al",
  "a", "mi", "tu", "su", "un", "una", "para", "por", "con", "sin",
  "que", "the", "of",
]);
function initialsFromTitle(title) {
  if (!title || typeof title !== "string") return "??";
  const clean = title.replace(/[^\p{L}\s]/gu, " ").trim();
  if (!clean) return "??";
  const words = clean.split(/\s+/).filter((w) => !STOPWORDS.has(w.toLowerCase()));
  const useWords = words.length > 0 ? words : clean.split(/\s+/);
  if (useWords.length === 1) {
    const w = useWords[0];
    return (w.slice(0, 2)).toUpperCase();
  }
  return (useWords[0][0] + useWords[1][0]).toUpperCase();
}

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", hour12: false });
    }
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return "Ayer";
    if (diffDays < 7) {
      const days = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
      return days[d.getDay()];
    }
    return d.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit" });
  } catch {
    return "";
  }
}

const FILTERS = [
  { id: "all", label: "Todas" },
  { id: "recent", label: "Recientes" },
  { id: "resolved", label: "Resueltas" },
  { id: "favorite", label: "Favoritas" },
];

export default function ConversationsListV2({
  conversations,
  currentConvId,
  convsLoading,
  onSelect,
  onCreate,
  onDelete,
  onRename,
  onPatch,                // (convId, patch) => Promise<void>  — favorite/status/tag
  onOpenLabels,           // (conv) => void  — open labels modal
  onOpenContext,          // (conv) => void  — open long-press context menu
  onLogout,              // () => void  — sign out user
  onOpenTemplates,        // () => void  — open Templates modal (was a header button)
  onEnterSelectMode,      // () => void  — enter bulk selection mode
  selectMode = false,
  selectedIds,
  onToggleSelected,
  onExitSelectMode,
  onBulkPatch,
  onBulkDelete,
  isDesktopSplit = false, // if true, render as split (no fullscreen overlay)
}) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let arr = conversations;
    if (filter === "favorite") arr = arr.filter((c) => c.favorite);
    else if (filter === "resolved") arr = arr.filter((c) => c.status === "resolved");
    else if (filter === "recent") arr = arr.slice(0, 20);
    if (q) {
      arr = arr.filter(
        (c) =>
          (c.title || "").toLowerCase().includes(q) ||
          (c.tag?.name || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [conversations, filter, query]);

  return (
    <div
      data-testid="conv-list-v2"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "transparent",
        color: "var(--pl-text)",
      }}
    >
      {/* ── Topbar: select-mode (bulk actions) or normal ── */}
      {selectMode ? (
        <header
          data-testid="conv-list-select-topbar"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 14px",
            borderBottom: "1px solid rgba(0, 200, 255, 0.18)",
            background: "rgba(0, 200, 255, 0.06)",
          }}
        >
          <button
            type="button"
            data-testid="select-cancel"
            onClick={onExitSelectMode}
            style={{ ...iconBtnStyle, color: "var(--pl-primary)", border: "none" }}
            aria-label="Cancelar selección"
          >
            ✕
          </button>
          <span style={{ flex: 1, color: "var(--pl-text-strong)", fontWeight: 700, fontSize: 14 }}>
            {selectedIds.size} seleccionada{selectedIds.size === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            data-testid="select-bulk-rename"
            onClick={() => {
              if (selectedIds.size !== 1) return;
              const id = Array.from(selectedIds)[0];
              const conv = conversations.find((c) => c.conv_id === id);
              if (!conv) return;
              const next = window.prompt("Nuevo nombre de la consulta:", conv.title || "");
              if (next && next.trim() && next.trim() !== conv.title) {
                onRename(id, next.trim());
              }
              if (onExitSelectMode) onExitSelectMode();
            }}
            disabled={selectedIds.size !== 1}
            title={selectedIds.size === 1 ? "Renombrar consulta" : "Selecciona solo 1 para renombrar"}
            style={{ ...iconBtnStyle, color: "var(--pl-primary)", border: "none", opacity: selectedIds.size !== 1 ? 0.4 : 1 }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button
            type="button"
            data-testid="select-bulk-label"
            onClick={() => {
              if (selectedIds.size === 0) return;
              // Open LabelsModal in "apply to selection" mode — uses a synthetic conv
              // whose conv_id is a special marker our parent recognizes. Simplest path:
              // use first selected as anchor and apply same tag via onBulkPatch when picked.
              if (onOpenLabels) {
                onOpenLabels({
                  conv_id: "__bulk__",
                  title: `Aplicar a ${selectedIds.size} consulta${selectedIds.size === 1 ? "" : "s"}`,
                  tag: null,
                  __bulkIds: Array.from(selectedIds),
                });
              }
            }}
            disabled={selectedIds.size === 0}
            title="Aplicar etiqueta a la selección"
            style={{ ...iconBtnStyle, color: "var(--pl-primary)", border: "none", opacity: selectedIds.size === 0 ? 0.4 : 1 }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
          </button>
          <button
            type="button"
            data-testid="select-bulk-resolve"
            onClick={() => onBulkPatch && onBulkPatch({ status: "resolved" })}
            disabled={selectedIds.size === 0}
            title="Marcar como resueltas"
            style={{ ...iconBtnStyle, color: "#10b981", border: "none", opacity: selectedIds.size === 0 ? 0.4 : 1 }}
          >
            ✓
          </button>
          <button
            type="button"
            data-testid="select-bulk-favorite"
            onClick={() => onBulkPatch && onBulkPatch({ favorite: true })}
            disabled={selectedIds.size === 0}
            title="Marcar como favoritas"
            style={{ ...iconBtnStyle, color: "var(--pl-primary)", border: "none", opacity: selectedIds.size === 0 ? 0.4 : 1 }}
          >
            ★
          </button>
          <button
            type="button"
            data-testid="select-bulk-delete"
            onClick={() => onBulkDelete && onBulkDelete()}
            disabled={selectedIds.size === 0}
            title="Eliminar seleccionadas"
            style={{ ...iconBtnStyle, color: "#ef4444", border: "none", opacity: selectedIds.size === 0 ? 0.4 : 1 }}
          >
            🗑
          </button>
        </header>
      ) : (
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 14px",
          minHeight: 64,
          borderBottom: "1px solid rgba(0, 200, 255, 0.18)",
        }}
      >
        <button
          type="button"
          aria-label="Más opciones"
          data-testid="conv-list-menu"
          onClick={() => setMenuOpen((v) => !v)}
          style={iconBtnStyle}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="var(--pl-text-muted)">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          <span
            style={{
              fontWeight: 800,
              fontSize: 24,
              color: "var(--pl-primary)",
              letterSpacing: "0.05em",
              border: "2px solid var(--pl-primary)",
              borderRadius: 8,
              padding: "2px 8px",
              lineHeight: 1,
            }}
          >
            PL
          </span>
          <span
            style={{
              fontSize: 13,
              letterSpacing: "0.20em",
              color: "var(--pl-text-muted)",
              fontWeight: 700,
            }}
          >
            ASISTENTE VIRTUAL
          </span>
        </div>
        <button
          type="button"
          data-testid="conv-list-new"
          onClick={onCreate}
          aria-label="Nueva consulta"
          title="Nueva consulta"
          style={{
            ...iconBtnStyle,
            border: "1.5px solid var(--pl-primary)",
            color: "var(--pl-primary)",
          }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="var(--pl-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      </header>
      )}

      {/* Dropdown menu (⋯ button) */}
      {menuOpen && (
        <>
          <div
            onClick={() => setMenuOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40, background: "transparent" }}
          />
          <div
            data-testid="conv-list-dropdown"
            style={{
              position: "absolute",
              top: 56,
              left: 12,
              zIndex: 50,
              minWidth: 220,
              background: "var(--pl-card)",
              border: "1px solid rgba(0, 200, 255, 0.22)",
              borderRadius: 14,
              boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
              padding: 6,
              animation: "legalBubbleIn 140ms ease-out both",
            }}
          >
            <DropItem
              testId="dd-select"
              icon={
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="var(--pl-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              }
              label="Seleccionar consultas"
              onClick={() => {
                setMenuOpen(false);
                if (onEnterSelectMode) onEnterSelectMode();
              }}
            />
            <DropItem
              testId="dd-templates"
              icon={
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="var(--pl-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              }
              label="Plantillas legales"
              onClick={() => {
                setMenuOpen(false);
                if (onOpenTemplates) onOpenTemplates();
              }}
            />
            <DropItem
              testId="dd-labels"
              icon={
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="var(--pl-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
              }
              label="Etiquetas"
              onClick={() => {
                setMenuOpen(false);
                if (onOpenLabels) onOpenLabels(null);
              }}
            />
            <div style={{ height: 1, background: "var(--pl-border)", margin: "4px 6px" }} />
            <DropItem
              testId="dd-logout"
              icon={
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              }
              label="Cerrar sesión"
              danger
              onClick={() => {
                setMenuOpen(false);
                if (onLogout) onLogout();
              }}
            />
          </div>
        </>
      )}

      <h1
        style={{
          fontSize: 28,
          fontWeight: 800,
          letterSpacing: "-0.01em",
          padding: "16px 16px 8px",
          margin: 0,
          color: "var(--pl-text-strong)",
        }}
      >
        Consultas
      </h1>

      {/* Search pill */}
      <div style={{ padding: "0 16px 10px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            borderRadius: 999,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid var(--pl-border)",
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="2.2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <line x1="16.5" y1="16.5" x2="22" y2="22" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar"
            data-testid="conv-list-search"
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--pl-text)",
              fontSize: 14,
            }}
          />
        </div>
      </div>

      {/* Filter tabs */}
      <div
        style={{
          padding: "0 16px 10px",
          display: "flex",
          gap: 8,
          overflowX: "auto",
          scrollbarWidth: "none",
        }}
        className="conv-list-filters"
      >
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            data-testid={`conv-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            style={{
              padding: "6px 14px",
              borderRadius: 999,
              border: filter === f.id ? "1px solid var(--pl-primary)" : "1px solid var(--pl-border)",
              background: filter === f.id ? "rgba(0,200,255,0.10)" : "transparent",
              color: filter === f.id ? "var(--pl-primary)" : "var(--pl-text-muted)",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Items */}
      <div
        data-testid="conv-list-items"
        style={{
          flex: 1,
          overflowY: "auto",
          paddingBottom: 16,
        }}
      >
        {convsLoading && conversations.length === 0 && (
          <div style={emptyStateStyle}>Cargando consultas…</div>
        )}
        {!convsLoading && filtered.length === 0 && (
          <div style={emptyStateStyle}>
            {conversations.length === 0
              ? "Aún no tienes consultas. Toca el botón 💬 para iniciar una."
              : "No hay consultas que coincidan con este filtro."}
          </div>
        )}
        {filtered.map((c) => (
          <ConversationItemV2
            key={c.conv_id}
            conv={c}
            active={c.conv_id === currentConvId}
            selectMode={selectMode}
            selected={selectedIds?.has?.(c.conv_id)}
            onToggleSelected={onToggleSelected}
            onSelect={() => onSelect(c.conv_id)}
            onDelete={() => onDelete(c.conv_id)}
            onRename={onRename}
            onPatch={onPatch}
            onOpenContext={onOpenContext}
          />
        ))}
      </div>
    </div>
  );
}

const iconBtnStyle = {
  width: 36,
  height: 36,
  borderRadius: 999,
  border: "1px solid var(--pl-border)",
  background: "transparent",
  color: "var(--pl-text-muted)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  flexShrink: 0,
};

const emptyStateStyle = {
  padding: "32px 24px",
  textAlign: "center",
  color: "var(--pl-text-muted)",
  fontSize: 13,
  lineHeight: 1.5,
};

function DropItem({ testId, icon, label, hint, danger, onClick }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "10px 12px",
        background: "transparent",
        border: "none",
        textAlign: "left",
        color: danger ? "#ef4444" : "var(--pl-text)",
        fontSize: 14,
        fontWeight: 500,
        cursor: "pointer",
        borderRadius: 8,
      }}
      onMouseOver={(e) => { e.currentTarget.style.background = "rgba(0, 200, 255, 0.06)"; }}
      onMouseOut={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <span style={{ width: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </span>
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      {hint && (
        <span style={{ fontSize: 11, color: "var(--pl-text-muted)", fontStyle: "italic", flexShrink: 0 }}>
          {hint}
        </span>
      )}
    </button>
  );
}

// ─── Single conversation item with swipe-to-reveal actions ──────────────────
function ConversationItemV2({ conv, active, onSelect, onDelete, onRename, onPatch, onOpenContext, selectMode, selected, onToggleSelected }) {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const intentRef = useRef(null); // 'horizontal' | 'vertical' | null
  const longPressTimer = useRef(null);
  const movedRef = useRef(false);
  const ACTIONS_WIDTH = 160; // 80px renombrar + 80px eliminar
  const TRIGGER_THRESHOLD = 60;

  const initials = initialsFromTitle(conv.title);
  const time = relativeTime(conv.updated_at || conv.created_at);
  const isFavorite = !!conv.favorite;
  const isResolved = conv.status === "resolved";
  const tag = conv.tag;

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    movedRef.current = false;
    intentRef.current = null;
    setDragging(true);
    clearTimeout(longPressTimer.current);
    longPressTimer.current = setTimeout(() => {
      if (!movedRef.current && onOpenContext) {
        if (navigator.vibrate) try { navigator.vibrate(18); } catch { /* noop */ }
        onOpenContext(conv);
      }
    }, 480);
  };

  const onTouchMove = (e) => {
    if (!dragging) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;
    if (!movedRef.current && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      movedRef.current = true;
      clearTimeout(longPressTimer.current);
      intentRef.current = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
    }
    if (intentRef.current === "horizontal") {
      // Only allow swipe left (negative)
      const clamped = Math.max(-ACTIONS_WIDTH, Math.min(0, dx));
      setDragX(clamped);
    }
  };

  const onTouchEnd = () => {
    clearTimeout(longPressTimer.current);
    setDragging(false);
    if (Math.abs(dragX) >= TRIGGER_THRESHOLD) {
      // Snap open
      setDragX(-ACTIONS_WIDTH);
    } else {
      setDragX(0);
    }
  };

  const closeSwipe = () => setDragX(0);

  const handleRenamePrompt = () => {
    closeSwipe();
    const next = window.prompt("Nuevo nombre de la consulta:", conv.title || "");
    if (next && next.trim() && next.trim() !== conv.title) {
      onRename(conv.conv_id, next.trim());
    }
  };

  const handleDelete = () => {
    closeSwipe();
    if (window.confirm(`¿Eliminar "${conv.title || 'esta consulta'}"?`)) {
      onDelete();
    }
  };

  return (
    <div
      data-testid={`conv-item-${conv.conv_id}`}
      style={{
        position: "relative",
        overflow: "hidden",
        borderBottom: "1px solid var(--pl-border)",
        background: active ? "rgba(0,200,255,0.06)" : "transparent",
      }}
    >
      {/* Active indicator (left bar) */}
      {active && (
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: "var(--pl-primary)",
            zIndex: 2,
          }}
        />
      )}
      {/* Swipe actions revealed underneath — only render when actively dragging or open */}
      {dragX !== 0 && (
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: ACTIONS_WIDTH,
          display: "flex",
        }}
      >
        <button
          type="button"
          onClick={handleRenamePrompt}
          data-testid={`conv-swipe-rename-${conv.conv_id}`}
          style={{
            flex: 1,
            border: "none",
            background: "#f59e0b",
            color: "#fff",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          Renombrar
        </button>
        <button
          type="button"
          onClick={handleDelete}
          data-testid={`conv-swipe-delete-${conv.conv_id}`}
          style={{
            flex: 1,
            border: "none",
            background: "#ef4444",
            color: "#fff",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4h6v2" />
          </svg>
          Eliminar
        </button>
      </div>
      )}
      {/* The item itself */}
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={() => {
          if (selectMode) {
            if (onToggleSelected) onToggleSelected(conv.conv_id);
            return;
          }
          if (dragX !== 0) {
            closeSwipe();
            return;
          }
          onSelect();
        }}
        onContextMenu={(e) => {
          // Desktop right-click → open the same context menu used for long-press on mobile
          e.preventDefault();
          if (onOpenContext) onOpenContext(conv);
        }}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "16px 16px",
          background: "transparent",
          cursor: "pointer",
          transform: `translateX(${dragX}px)`,
          transition: dragging ? "none" : "transform 200ms ease",
          zIndex: 1,
          touchAction: "pan-y",
        }}
      >
        <div style={{ position: "relative", flexShrink: 0 }}>
          {selectMode ? (
            <div
              role="checkbox"
              aria-checked={!!selected}
              data-testid={`conv-checkbox-${conv.conv_id}`}
              style={{
                width: 28,
                height: 28,
                borderRadius: 999,
                border: selected ? "2px solid var(--pl-primary)" : "2px solid var(--pl-border-strong)",
                background: selected ? "var(--pl-primary)" : "transparent",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#001018",
                fontWeight: 700,
                marginRight: 28,
              }}
            >
              {selected && "✓"}
            </div>
          ) : null}
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: tag?.color ? `${tag.color}22` : "rgba(0,200,255,0.10)",
              border: `1.5px solid ${tag?.color || "var(--pl-primary)"}`,
              display: selectMode ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ffffff",
              fontWeight: 800,
              fontSize: 17,
              letterSpacing: "0.05em",
            }}
          >
            {initials}
          </div>
          {/* Online dot — bot is always online */}
          {!selectMode && (
            <div
              style={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: 13,
                height: 13,
                borderRadius: 999,
                background: "#10b981",
                border: "2.5px solid var(--pl-card)",
              }}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                color: active ? "var(--pl-primary)" : "#ffffff",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {conv.title || "Sin título"}
            </span>
            <span style={{ fontSize: 12, color: active ? "var(--pl-primary)" : "var(--pl-text-muted)", flexShrink: 0, fontWeight: 600 }}>
              {time}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            {tag?.color && (
              <span style={{
                width: 8, height: 8, borderRadius: 999,
                background: tag.color, flexShrink: 0,
              }} />
            )}
            <span
              style={{
                color: "var(--pl-text-muted)",
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {conv.last_preview || tag?.name || " "}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              {isFavorite && (
                <span style={{ color: "var(--pl-primary)", fontSize: 14 }} aria-label="Favorita">★</span>
              )}
              {isResolved && (
                <span style={{ color: "#10b981", fontSize: 14, fontWeight: 700 }} aria-label="Resuelta">✓</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
